import React, { useEffect, useState } from "react";
import { Button, Form, Input, Radio, Select, Space, message } from "antd";
import TemplatePreview from "./TemplatePreview";
import { buildComponents, extractTemplateFields, renderTemplateText, parseTemplateText } from "./TemplateUtils";
import styles from "./templates.module.css";

const buttonTypes = [
  { value: "QUICK_REPLY", label: "Custom" },
  { value: "URL", label: "Visit website" },
  { value: "PHONE_NUMBER", label: "Call phone number" },
  { value: "COPY_CODE", label: "Copy offer code" },
];

const categorySubtypes = {
  MARKETING: [
    { value: "DEFAULT", label: "Default", desc: "Send messages with media and customised buttons to engage your customers." },
    { value: "CATALOGUE", label: "Catalogue", desc: "Send messages that drive sales by connecting your product catalogue." },
    { value: "CALLING_PERMISSIONS_REQUEST", label: "Calling permissions request", desc: "Ask customers if you can call them on WhatsApp" },
  ],
  UTILITY: [
    { value: "DEFAULT", label: "Default", desc: "Send messages about an existing order or account." },
    { value: "CALLING_PERMISSIONS_REQUEST", label: "Calling permissions request", desc: "Ask customers if you can call them on WhatsApp" },
  ],
  AUTHENTICATION: [
    { value: "ONE_TIME_PASSCODE", label: "One-time passcode", desc: "Send codes to verify a transaction or login." },
  ]
};

export default function TemplateEditor({ open, template, messaging, onCancel, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState("MARKETING");
  const [buttons, setButtons] = useState([]);
  const [examples, setExamples] = useState({});
  const [previewMode, setPreviewMode] = useState("message");

  useEffect(() => {
    if (!open) return;
    const fields = template ? extractTemplateFields(template) : {};
    setCategory(template?.category || "MARKETING");
    setButtons(fields.buttons || []);
    setPreviewMode("message");
    form.setFieldsValue({
      name: template?.name || "",
      language: template?.language || "en",
      category: template?.category || "MARKETING",
      variableType: "NUMBER",
      mediaSample: fields.mediaSample || "NONE",
      header: fields.header || "",
      body: fields.body || (template?.isFromLibrary ? template.body : "Hello"),
      footer: fields.footer || (template?.isFromLibrary ? template.footer : ""),
      // Auth specific
      auth_code_type: "ONE_TIME_PASSCODE",
      auth_delivery_type: "COPY_CODE",
    });
    setExamples(fields.examples || {});
  }, [open, template, form]);

  useEffect(() => {
    const currentSubtype = form.getFieldValue("subtype");
    const validSubtypes = (categorySubtypes[category] || []).map(s => s.value);
    if (!validSubtypes.includes(currentSubtype)) {
      form.setFieldsValue({ subtype: validSubtypes[0] });
    }
  }, [category, form]);

  const values = Form.useWatch([], form) || {};

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        name: v.name,
        language: v.language,
        category: v.category,
        components: buildComponents({
          header: v.header,
          body: v.body,
          footer: v.footer,
          buttons,
          mediaSample: v.mediaSample,
          examples,
        }),
      };
      if (template?.id) {
        await messaging.updateTemplate(template.id, payload);
      } else {
        await messaging.createTemplate(undefined, payload);
      }
      message.success(template ? "Template updated" : "Template created and submitted for review");
      onSaved?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || err.message || "Template save failed");
    } finally {
      setSaving(false);
    }
  };

  const addButton = (type) => {
    const option = buttonTypes.find((b) => b.value === type);
    setButtons((prev) => [...prev, { type, text: option?.label || "Button", url: "", phone_number: "" }]);
  };

  const updateButton = (idx, field, value) => {
    setButtons((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b))
    );
  };

  const detectedVars = [
    ...parseTemplateText(values.header || "").variables.map(v => ({ key: v, scope: 'header' })),
    ...parseTemplateText(values.body || "").variables.map(v => ({ key: v, scope: 'body' })),
    ...buttons.filter(b => b.type === "URL" && b.url).flatMap(b => parseTemplateText(b.url).variables.map(v => ({ key: v, scope: 'button', url: b.url })))
  ];

  // Generate rendered versions for the preview
  const renderedHeader = renderTemplateText(values.header || "", examples); 
  const renderedBody = renderTemplateText(values.body || "", examples);

  const editorActions = (
    <div className={styles.editorActions}>
      <Button onClick={onCancel}>Discard</Button>
      <Button type="primary" loading={saving} onClick={save}>Submit for review</Button>
    </div>
  );

  return (
    <div className={styles.editorContainer}>
      <div className={styles.editorLayout}>
        <Form form={form} layout="vertical" className={styles.editorForm}>
          {editorActions}

          <section className={styles.panel}>
            <h3>Set up your template</h3>
            <Form.Item name="category" initialValue={category} label="Category">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                onChange={(e) => setCategory(e.target.value)}
                options={[
                  { label: "Marketing", value: "MARKETING" },
                  { label: "Utility", value: "UTILITY" },
                  { label: "Authentication", value: "AUTHENTICATION" },
                ]}
              />
            </Form.Item>

            <Form.Item name="subtype" label="Template Subtype" rules={[{ required: true }]}>
              <Radio.Group className={styles.verticalRadio}>
                {(categorySubtypes[category] || []).map(s => (
                  <Radio key={s.value} value={s.value}>
                    <div className={styles.subtypeItem}>
                      <strong>{s.label}</strong>
                      <div className={styles.subtypeDesc}>{s.desc}</div>
                    </div>
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </section>

          <section className={styles.panel}>
            <h3>Template name and language</h3>
            <div className={styles.formRow}>
              <Form.Item
                name="name"
                label="Name your template"
                rules={[
                  { required: true, message: "Template name is required" },
                  { pattern: /^[a-z0-9_]+$/, message: "Lowercase alphanumeric and underscores only" }
                ]}
              >
                <Input placeholder="Enter a template name" maxLength={512} disabled={!!template?.id} />
              </Form.Item>
              <Form.Item name="language" label="Select language">
                <Select options={[{ value: "en", label: "English" }, { value: "en_US", label: "English (US)" }]} />
              </Form.Item>
            </div>
          </section>

          <section className={styles.panel}>
            <h3>Content</h3>
            <div className={`${styles.formRow} ${styles.formRowSmall}`}>
              <Form.Item name="variableType" label="Type of variable">
                <Select options={[{ value: "NUMBER", label: "Number" }, { value: "NAME", label: "Name" }]} />
              </Form.Item>
              <Form.Item name="mediaSample" label="Media sample">
                <Select
                  options={[
                    { value: "NONE", label: "None" },
                    { value: "IMAGE", label: "Image" },
                    { value: "VIDEO", label: "Video" },
                    { value: "DOCUMENT", label: "Document" },
                  ]}
                />
              </Form.Item>
            </div>
            <Form.Item name="header" label="Header">
              <Input placeholder="Add a short line of text to the header" maxLength={60} />
            </Form.Item>
            <Form.Item
              name="body"
              label="Body"
              rules={[{ required: true, message: "Body is required" }]}
            >
              <Input.TextArea rows={6} maxLength={1024} placeholder="Hello {{name}}, your order {{order_id}} is ready!" />
            </Form.Item>
            <Form.Item name="footer" label="Footer">
              <Input placeholder="Add a short line of text to the bottom" maxLength={60} />
            </Form.Item>

            {detectedVars.length > 0 && (
              <div className={styles.examplesSection}>
                <h4>Variable Examples</h4>
                <p className={styles.helpText}>To submit a template with variables, you must provide examples for each one.</p>
                {detectedVars.map((v, i) => (
                  <div key={`${v.scope}-${v.key}-${i}`} className={styles.exampleRow}>
                    <label>{"{{" + v.key + "}}"}</label>
                    <Input 
                      placeholder={`Example for ${v.key}`}
                      value={examples[`${v.scope}:${v.key}`] || ""}
                      onChange={(e) => setExamples(prev => ({ ...prev, [`${v.scope}:${v.key}`]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>

          {category === "AUTHENTICATION" && (
            <section className={styles.panel}>
              <h3>Authentication Details</h3>
              <Form.Item name="auth_code_type" label="Code Type">
                <Select options={[{ value: "ONE_TIME_PASSCODE", label: "One-time passcode" }]} />
              </Form.Item>
              <Form.Item name="auth_delivery_type" label="Delivery Method">
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="COPY_CODE">Copy code</Radio>
                    <Radio value="ONE_TAP">One-tap auto-fill (Android)</Radio>
                    <Radio value="ZERO_TAP">Zero-tap auto-fill (Android)</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              <div className={styles.formRow}>
                <Form.Item name="package_name" label="Package name">
                  <Input placeholder="com.example.app" />
                </Form.Item>
                <Form.Item name="app_hash" label="App signature hash">
                  <Input placeholder="11 characters" maxLength={11} />
                </Form.Item>
              </div>
            </section>
          )}

          <section className={styles.panel}>
            <h3>Buttons</h3>
            <Space wrap>
              {buttonTypes.map((b) => (
                <Button key={b.value} onClick={() => addButton(b.value)}>
                  + {b.label}
                </Button>
              ))}
            </Space>
            {buttons.map((b, idx) => (
              <div className={styles.buttonEditGroup} key={`${b.type}-${idx}`}>
                <div className={styles.buttonHeader}>
                  <strong>{buttonTypes.find(bt => bt.value === b.type)?.label}</strong>
                  <Button type="link" danger size="small" onClick={() => setButtons((prev) => prev.filter((_, i) => i !== idx))}>
                    Remove
                  </Button>
                </div>
                <div className={styles.buttonFields}>
                  <Form.Item label="Button Text" className={styles.flex1}>
                    <Input
                      value={b.text}
                      onChange={(e) => updateButton(idx, "text", e.target.value)}
                      maxLength={25}
                    />
                  </Form.Item>
                  {b.type === "URL" && (
                    <Form.Item label="Website URL" className={styles.flex2}>
                      <Input
                        value={b.url}
                        onChange={(e) => updateButton(idx, "url", e.target.value)}
                        placeholder="https://example.com/{{1}}"
                      />
                    </Form.Item>
                  )}
                  {b.type === "PHONE_NUMBER" && (
                    <Form.Item label="Phone Number" className={styles.flex2}>
                      <Input
                        value={b.phone_number}
                        onChange={(e) => updateButton(idx, "phone_number", e.target.value)}
                        placeholder="+1 555 123 4567"
                      />
                    </Form.Item>
                  )}
                  {b.type === "COPY_CODE" && (
                    <Form.Item label="Offer Code" className={styles.flex2}>
                      <Input
                        value={b.example}
                        onChange={(e) => updateButton(idx, "example", e.target.value)}
                        placeholder="SUMMER20"
                      />
                    </Form.Item>
                  )}
                </div>
              </div>
            ))}
          </section>
          
          {editorActions}
        </Form>
        <div className={styles.sendPreviewPanel}>
          <TemplatePreview
            header={renderedHeader}
            body={renderedBody}
            footer={values.footer}
            buttons={buttons}
            originalHeader={values.header}
            originalBody={values.body}
            mode={previewMode}
            onModeChange={setPreviewMode}
          />
        </div>
      </div>
    </div>
  );
}

